import React, { useState } from 'react';
import './ConsumerSolutionPage.css';

const fallbackImage = '/dts-assets/ai/consumer-hero.png';

const handleImageError = (fallback) => (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallback;
};

const HeroBlock = ({ label, title, text, image, chipOne = 'Premium App', chipTwo = 'Smart Reports' }) => (
  <section className="enhanced-hero" style={{ backgroundImage: `url(${image})` }}>
    <div className="container enhanced-hero-grid">
      <div className="enhanced-copy reveal-left">
        <span className="section-label eyebrow-dark">{label}</span>
        <h1 dangerouslySetInnerHTML={{ __html: title }} />
        <p>{text}</p>

        <div className="hero-actions">
          <a href="/contact-us" className="btn btn-primary">Get Free Consultation</a>
          <a href="/packages" className="btn btn-outline">View Packages</a>
        </div>

        <div className="hero-metrics">
          <span><strong>App</strong>Tracking</span>
          <span><strong>Trip</strong>Reports</span>
          <span><strong>Safety</strong>Alerts</span>
        </div>
      </div>

      <div className="hero-visual-wrap reveal">
        <div className="floating-orbit" />
        <div className="float-chip one"><b />{chipOne}</div>
        <div className="float-chip two"><b />{chipTwo}</div>
        <div className="hero-visual">
          <img src={image} alt={label} onError={handleImageError(fallbackImage)} />
        </div>
      </div>
    </div>
  </section>
);

const consumerFeatures = [
  {
    title: 'Premium Mobile App',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=900&q=80',
    text: 'Track your vehicle live, check status, view history, and manage alerts directly from your smartphone.',
    details: ['Live vehicle location on mobile', 'Vehicle status and movement updates', 'History playback access', 'Smart alerts and notifications']
  },
  {
    title: 'Reports',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
    text: 'Our mobile reporting features give you essential information about your car at your fingertips.',
    details: ['Daily vehicle activity reports', 'Mileage and movement records', 'Alert history reports', 'Useful information for vehicle owners']
  },
  {
    title: 'Trip Reports',
    image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
    text: 'Trip reports give valuable information about each trip of your car including route, mileage, distance, and travel history.',
    details: ['Trip start and end details', 'Route and travel history', 'Distance and mileage view', 'Useful trip-wise information']
  },
  {
    title: 'Violations Report',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=900&q=80',
    text: 'Violation reports highlight driving issues such as seatbelt, mileage, speed, fatigue, rest-time, and other driving violations.',
    details: ['Seatbelt violation report', 'Delayed seatbelt alerts', 'Speed violation report', 'Fatigue and rest-time violation report'],
    note: 'The type of violations reported depends on the hardware and sensors installed in the vehicle. Some hardware and sensors may have an additional cost.'
  }
];

const DetailModal = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <div className="solution-modal-backdrop" onClick={onClose}>
      <div className="solution-modal" onClick={(e) => e.stopPropagation()}>
        <button className="solution-modal-close" onClick={onClose} type="button">×</button>

        <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />

        <div className="solution-modal-content">
          <span className="section-label">Consumer Solution</span>
          <h2>{item.title}</h2>
          <p>{item.text}</p>

          <ul>
            {item.details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>

          {item.note && <p className="modal-note">{item.note}</p>}

          <a href="/contact-us" className="btn btn-primary">Get This Solution</a>
        </div>
      </div>
    </div>
  );
};

const ConsumerSolutionPage = () => {
  const [selectedFeature, setSelectedFeature] = useState(null);

  return (
    <main className="enhanced-page consumer-enhanced">
      <HeroBlock
        label="Consumer Solutions"
        title="Premium Mobile App & <em>Smart Reports</em>"
        text="DTS consumer solutions are designed for personal vehicle owners who need mobile tracking, trip reports, driving violation reports, and reliable vehicle security."
        image="/dts-assets/ai/consumer-hero.png"
      />

      <section className="enhanced-section alt">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">Consumer Solutions</span>
            <h2>Complete Vehicle Information <em>On Your Fingertips</em></h2>
            <div className="divider" />
            <p>
              Monitor your vehicle through premium mobile app features, reports,
              trip history, and safety violation insights.
            </p>
          </div>

          <div className="solution-media-grid consumer-media-grid">
            {consumerFeatures.map((item) => (
              <button
                className="solution-media-card reveal"
                key={item.title}
                type="button"
                onClick={() => setSelectedFeature(item)}
              >
                <div className="solution-media-img">
                  <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />
                </div>

                <div className="solution-media-body">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <span>View Details</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="enhanced-section dark-band">
        <div className="container process-grid">
          {[
            ['01', 'Track', 'View live vehicle location through mobile and web access.'],
            ['02', 'Analyze', 'Check trip reports, mileage, route history, and daily activity.'],
            ['03', 'Alert', 'Receive important alerts based on vehicle movement and installed sensors.'],
            ['04', 'Protect', 'Improve vehicle security with smart monitoring and emergency support.']
          ].map((item) => (
            <div className="process-card reveal" key={item[1]}>
              <span>{item[0]}</span>
              <h3>{item[1]}</h3>
              <p>{item[2]}</p>
            </div>
          ))}
        </div>
      </section>

      <DetailModal item={selectedFeature} onClose={() => setSelectedFeature(null)} />
    </main>
  );
};

export default ConsumerSolutionPage;